import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Plus, Share2 } from 'lucide-react';
import { apiService } from '../services/api';
import browserCacheService from '../services/BrowserCacheService';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../components/notifications';
import Skeleton from '../components/ui/Skeleton';
import EmptyState from '../components/ui/EmptyState.jsx';
import PageShell from '../components/ui/PageShell';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card.jsx';
import SegmentedControl from '../components/ui/SegmentedControl.jsx';
import DraftFeedbackBanner from '../components/DraftFeedbackBanner';
import { SESSION_STATUS } from '../constants/SessionTypes';
import JournalCard from '../components/journal/JournalCard';
import ManualEntryForm from '../components/journal/ManualEntryForm';
import ShareJournalDialog from '../components/journal/ShareJournalDialog';
import GoalsList from '../components/journal/GoalsList';
import { buildJournalTimeline } from '../components/journal/journalUtils';
import { useConfirm } from '../components/ui/ConfirmProvider.jsx';

/**
 * Lernlogbuch V2 — das EINE schlanke Logbuch.
 * `embedded`: als MainScreen-View (Bottom-Nav-Tab), ohne eigenen Seitenrahmen.
 * Standalone (Route /learning-journal): dünner Seiten-Wrapper mit Zurück-Button.
 * Bewusst ohne Analytics-Diagramme, Druck/PDF und Sidebar der V1.
 */
const LearningJournalPageV2 = ({ embedded = false }) => {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();

  // Gleicher SWR-Cache wie V1 → sofortige Anzeige, Refresh im Hintergrund
  const journalCacheKey = currentUser?.id ? `journal_${currentUser.id}` : null;
  const [cachedJournal] = useState(() => (journalCacheKey ? browserCacheService.get(journalCacheKey) : null));

  const [sessions, setSessions] = useState(cachedJournal?.sessions || []);
  const [manualEntries, setManualEntries] = useState(cachedJournal?.manualEntries || []);
  const [isLoading, setIsLoading] = useState(!cachedJournal);
  const [activeTab, setActiveTab] = useState('entries');
  const [entryFormOpen, setEntryFormOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Cache mit dem aktuellen State synchron halten (auch lokale Mutationen)
  useEffect(() => {
    if (!journalCacheKey || isLoading) return;
    // Ziele sind nicht mehr Teil des Journal-Caches — GoalsList lädt sie selbst
    browserCacheService.set(
      journalCacheKey,
      { sessions, manualEntries },
      browserCacheService.TTL.USER_PROFILE
    );
  }, [journalCacheKey, isLoading, sessions, manualEntries]);

  useEffect(() => {
    let cancelled = false;
    const loadJournalData = async () => {
      try {
        const [sessionsResponse, entriesResponse] = await Promise.all([
          apiService.getLearningJournalSessions(),
          apiService.getJournalEntries().catch(() => ({ success: false }))
        ]);
        if (cancelled) return;

        // Karten starten bewusst eingeklappt — aufgeklappt füllt die oberste
        // Session den halben Screen, bevor man die Liste überhaupt sieht.
        if (sessionsResponse.success && sessionsResponse.data) {
          setSessions(sessionsResponse.data);
        }
        if (entriesResponse.success && Array.isArray(entriesResponse.data)) {
          setManualEntries(entriesResponse.data);
        }
      } catch (error) {
        console.error('Fehler beim Laden des Logbuchs:', error);
        toast.error('Fehler beim Laden des Lernlogbuchs', { haptic: false });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadJournalData();
    return () => { cancelled = true; };
  }, []);

  const completedSessions = useMemo(
    () => sessions.filter(s => s.status === SESSION_STATUS.COMPLETED),
    [sessions]
  );

  // Sessions + manuelle Einträge chronologisch gemischt (gleiche Reihenfolge
  // wie in der geteilten Ansicht, deshalb ein gemeinsamer Helfer)
  const journalTimeline = useMemo(
    () => buildJournalTimeline(completedSessions, manualEntries),
    [completedSessions, manualEntries]
  );

  const stats = useMemo(() => {
    const totalLearningTime = completedSessions.reduce((sum, s) => sum + (s.duration || 0), 0);
    const totalAura = completedSessions.reduce((sum, s) => sum + (s.auraEarned || 0), 0);
    const rated = completedSessions.filter(s => s.rating > 0);
    const averageRating = rated.length > 0
      ? (rated.reduce((sum, s) => sum + s.rating, 0) / rated.length).toFixed(1)
      : null;
    return { sessionCount: completedSessions.length, totalLearningTime, totalAura, averageRating };
  }, [completedSessions]);

  // Manuellen Eintrag speichern (neu oder bearbeitet) — inkl. File-Upload (aus V1)
  const handleSaveEntry = async (formData) => {
    const { _files: newFiles = [], _removedFileIds: removedFileIds = [], ...entryData } = formData;

    let savedEntry;
    if (editingEntry) {
      for (const fileId of removedFileIds) {
        try {
          await apiService.deleteJournalEntryFile(editingEntry.id, fileId);
        } catch (err) {
          console.warn('Datei konnte nicht gelöscht werden:', err);
        }
      }
      const response = await apiService.updateJournalEntry(editingEntry.id, entryData);
      if (response.success) savedEntry = response.data;
    } else {
      const response = await apiService.createJournalEntry(entryData);
      if (response.success) savedEntry = response.data;
    }

    if (!savedEntry) return;

    if (newFiles.length > 0) {
      try {
        const fd = new FormData();
        newFiles.forEach(f => fd.append('files', f));
        const uploadResp = await apiService.uploadJournalEntryFiles(savedEntry.id, fd);
        if (uploadResp.files) {
          savedEntry.files = [...(savedEntry.files || []), ...uploadResp.files];
        }
      } catch (err) {
        console.error('Dateien konnten nicht hochgeladen werden:', err);
        toast.error('Eintrag gespeichert, aber Dateien konnten nicht hochgeladen werden');
      }
    }

    if (editingEntry) {
      setManualEntries(prev => prev.map(e => (e.id === editingEntry.id ? savedEntry : e)));
    } else {
      setManualEntries(prev => [savedEntry, ...prev]);
    }

    setEntryFormOpen(false);
    setEditingEntry(null);
    return savedEntry;
  };

  const handleDeleteEntry = async (entryId) => {
    if (!(await confirm({ title: 'Löschen?', text: 'Diesen Eintrag wirklich löschen?', variant: 'danger', confirmLabel: 'Löschen' }))) return;
    try {
      await apiService.deleteJournalEntry(entryId);
      setManualEntries(prev => prev.filter(e => e.id !== entryId));
    } catch (error) {
      toast.error('Eintrag konnte nicht gelöscht werden');
    }
  };

  // Einträge | Ziele als Segment (Okt 2026): Im Spot sitzen Kennzahlen und
  // Einträge in EINER Karte, die Ziele in einer zweiten. Mobil stehen sie
  // nicht untereinander (zu lang), sondern hinter einem ruhigen Umschalter.
  const sectionSwitch = (
    <SegmentedControl
      ariaLabel="Logbuch-Bereiche"
      idPrefix="journal"
      value={activeTab}
      onChange={setActiveTab}
      options={[
        { value: 'entries', label: 'Einträge' },
        { value: 'goals', label: 'Ziele' },
      ]}
    />
  );

  const content = isLoading ? (
    <div className="space-y-3" aria-busy="true">
      <span className="sr-only">Logbuch wird geladen …</span>
      <div className="h-11 rounded-xl bg-chip animate-pulse" aria-hidden="true" />
      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-4 gap-2">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="h-[66px] rounded-lg bg-surface-raised animate-pulse" aria-hidden="true" />
          ))}
        </div>
        {[0, 1, 2].map((i) => (
          <div key={i} className="space-y-2 border-t border-border pt-3" aria-hidden="true">
            <Skeleton.Line width="w-2/3" />
            <Skeleton.Line width="w-1/3" />
          </div>
        ))}
      </Card>
    </div>
  ) : (
    <div className="space-y-3">
      {sectionSwitch}

      {activeTab === 'entries' && (
        <div id="journal-panel-entries" role="tabpanel" aria-labelledby="journal-tab-entries">
          {journalTimeline.length === 0 ? (
            // Haupttabs zeigen Leerzustaende ohne eigenen Kasten (Okt 2026).
            <EmptyState
              icon={BookOpen}
              size="sm"
              text="Abgeschlossene Lernsessions erscheinen hier von selbst. Eigene Einträge legst du mit „Eintrag“ an."
            />
          ) : (
            <JournalCard
              timeline={journalTimeline}
              stats={stats}
              userId={currentUser?.id}
              onEditEntry={(entry) => {
                setEditingEntry(entry);
                setEntryFormOpen(true);
              }}
              onDeleteEntry={handleDeleteEntry}
            />
          )}
        </div>
      )}

      {activeTab === 'goals' && (
        <div id="journal-panel-goals" role="tabpanel" aria-labelledby="journal-tab-goals">
          <GoalsList />
        </div>
      )}

      {/* Logbuch per Link teilen (für Lerngespräche) */}
      <ShareJournalDialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} />

      {/* Formular für manuelle Einträge */}
      {entryFormOpen && (
        <ManualEntryForm
          entry={editingEntry}
          onSave={handleSaveEntry}
          onClose={() => {
            setEntryFormOpen(false);
            setEditingEntry(null);
          }}
        />
      )}
    </div>
  );

  // Ein Kopf für beide Fälle — nur der Rahmen unterscheidet sich.
  const headerActions = (
    <div className="flex items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={() => setShareDialogOpen(true)}
        aria-label="Logbuch teilen"
      >
        <Share2 className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline">Teilen</span>
      </Button>
      <Button
        size="sm"
        onClick={() => {
          setEditingEntry(null);
          setEntryFormOpen(true);
        }}
      >
        <Plus className="w-4 h-4" aria-hidden="true" />
        <span className="hidden sm:inline">Neuer Eintrag</span>
        <span className="sm:hidden">Eintrag</span>
      </Button>
    </div>
  );

  if (embedded) {
    return (
      <PageShell
        variant="main"
        embedded
        title="Lernlogbuch"
        meta={journalTimeline.length > 0 ? `${journalTimeline.length} ${journalTimeline.length === 1 ? 'Eintrag' : 'Einträge'}` : undefined}
        metaInline
        actions={headerActions}
        width="form"
      >
        {content}
      </PageShell>
    );
  }

  // Standalone (Route /learning-journal)
  return (
    <PageShell
      variant="main"
      title="Lernlogbuch"
      meta={journalTimeline.length > 0 ? `${journalTimeline.length} ${journalTimeline.length === 1 ? 'Eintrag' : 'Einträge'}` : undefined}
      metaInline
      onBack={() => navigate('/app')}
      backLabel="Zurück zur Startseite"
      actions={headerActions}
      width="form"
      background
    >
      <DraftFeedbackBanner />
      {content}
    </PageShell>
  );
};

LearningJournalPageV2.propTypes = {
  embedded: PropTypes.bool
};

export default LearningJournalPageV2;
